import { useRef } from "react";
import { Button, Dropdown } from "antd";
import { Icon } from "../icon/icon";
import useNotification from "../../components/notification/notification";

// legacy (docx-only) limit used in some screens
const MAX_DOCX_SIZE = 10 * 1024 * 1024; // 10MB

// new (pdf/docx) import limit
const MAX_IMPORT_SIZE = 25 * 1024 * 1024; // 25MB

export const DrawerHeader = ({
  title,
  subtitle,
  isEditing,
  onEdit,
  onClose,
  onSave,
  saveLabel = "Save",
  saveTestID, // LF360-1981: data-testid of the save button, for the browser tests
  saveDisabled = false,
  onCancel,
  menu,
  isLoading,
  badge,
  onDiscard,
  onPublish,
  isChanged,
  onViewChanges,
  onDelete, // () => void
  onToggleActive, // () => void
  isActive,
  publishButton,
  onlyView,
  onOpenTask, // () => void

  // import
  onImportFile, // (file) => void (preferred: pdf/docx)
  onImportDocx, // (file) => void (legacy: docx only)

  onDuplicate,
  extraRight,
  onPreviewInApp,
}) => {
  const { openNotification } = useNotification();

  // ---- legacy docx input (kept for backward compatibility) ----
  const fileDocxRef = useRef(null);
  const triggerDocx = () => fileDocxRef.current?.click();

  const handleDocxChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.size > MAX_DOCX_SIZE) {
      openNotification?.({
        type: "error",
        message: <p>File is too large. Max 10MB.</p>,
      });
      return;
    }

    const okExt = /\.docx$/i.test(file.name);
    if (!okExt) {
      openNotification?.({
        type: "error",
        message: <p>Only .docx files are supported.</p>,
      });
      return;
    }

    onImportDocx?.(file);
  };

  // ---- new import input (pdf/docx) ----
  const fileImportRef = useRef(null);
  const triggerImport = () => fileImportRef.current?.click();

  const handleImportChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.size > MAX_IMPORT_SIZE) {
      openNotification?.({
        type: "error",
        message: <p>File is too large. Max 25MB.</p>,
      });
      return;
    }

    const okExt = /\.(docx|pdf)$/i.test(file.name);
    if (!okExt) {
      openNotification?.({
        type: "error",
        message: <p>Only .docx or .pdf files are supported.</p>,
      });
      return;
    }

    // preferred universal callback
    if (onImportFile) {
      onImportFile(file);
      return;
    }

    // fallback legacy callback (docx only)
    if (onImportDocx) {
      const okDocx = /\.docx$/i.test(file.name);
      if (!okDocx) {
        openNotification?.({
          type: "error",
          message: <p>This importer supports only .docx in this screen.</p>,
        });
        return;
      }
      onImportDocx(file);
    }
  };

  // ---- menu items ----
  const changeItems = [];
  if (!onlyView && onViewChanges && isChanged) {
    changeItems.push({
      key: "view_changes",
      label: "View changes",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onViewChanges?.();
      },
    });
  }
  if (!onlyView && onDiscard && isChanged) {
    changeItems.push({
      key: "discard_changes",
      label: "Discard changes",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onDiscard?.();
      },
    });
  }

  const resourceItems = [];
  if (!onlyView && onPreviewInApp) {
    resourceItems.push({
      key: "preview_in_app",
      label: "Preview in app",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onPreviewInApp?.();
      },
    });
  }
  if (!onlyView && onToggleActive) {
    resourceItems.push({
      key: "toggle_active",
      label: isActive ? "Deactivate" : "Activate",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onToggleActive?.();
      },
    });
  }
  if (!onlyView && onDuplicate) {
    resourceItems.push({
      key: "duplicate",
      label: "Duplicate",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onDuplicate?.();
      },
    });
  }
  if (!onlyView && onDelete) {
    resourceItems.push({
      key: "delete",
      danger: true,
      label: "Delete",
      "data-testid": "drawer-menu-delete",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onDelete?.();
      },
    });
  }

  // Import menu item (preferred)
  const importFileItems =
    !onlyView && (onImportFile || onImportDocx)
      ? [
          {
            key: "import_file",
            label: "Import file (PDF/DOCX)",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              triggerImport();
            },
          },
        ]
      : [];

  // Legacy docx item (optional; keep only if you still want it visible separately)
  const importDocxItems =
    !onlyView && onImportDocx && !onImportFile
      ? [
          {
            key: "import_docx",
            label: "Import MS Word",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              triggerDocx();
            },
          },
        ]
      : [];

  const externalItems = !onlyView ? menu?.items ?? [] : [];

  let menuItems = [];

  if (onlyView) {
    if (onOpenTask) {
      menuItems.push({
        key: "open_task",
        label: "Open task",
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation();
          onOpenTask?.();
        },
      });
    }
  } else {
    if (externalItems.length) menuItems.push(...externalItems);

    const hasImport = importFileItems.length || importDocxItems.length;
    const hasChanges = changeItems.length > 0;
    const hasResources = resourceItems.length > 0;

    if (externalItems.length && (hasImport || hasChanges || hasResources)) {
      menuItems.push({ type: "divider" });
    }

    if (importFileItems.length) menuItems.push(...importFileItems);
    if (importDocxItems.length) menuItems.push(...importDocxItems);

    if (hasImport && (hasChanges || hasResources)) {
      menuItems.push({ type: "divider" });
    }

    if (hasChanges) menuItems.push(...changeItems);

    if (hasChanges && hasResources) {
      menuItems.push({ type: "divider" });
    }

    if (hasResources) menuItems.push(...resourceItems);
  }

  const hasAnyMenu = menuItems.length > 0;

  const mergedMenuProps = hasAnyMenu
    ? {
        ...menu,
        items: menuItems,
        onClick: (info) => {
          info.domEvent?.stopPropagation?.();
          menu?.onClick?.(info);
        },
      }
    : undefined;

  return (
    <div className="drawer-header">
      <div className="drawer-header__left">
        <div className="drawer-header__titles">
          {badge && (
            <p className="drawer-header__badge">
              {typeof badge === "string" ? badge : badge}
            </p>
          )}
          {typeof title === "string" ? (
            <h2 className="drawer-header__title">{title}</h2>
          ) : (
            title
          )}
          {subtitle && (
            <p className="drawer-header__subtitle">
              {typeof subtitle === "string" ? subtitle : subtitle}
            </p>
          )}
        </div>
      </div>

      <div className="drawer-header__right">
        {extraRight ? extraRight : null}

        {hasAnyMenu && (
          <Dropdown trigger={["click"]} menu={mergedMenuProps}>
            <Button
              type="text"
              size="middle"
              icon={<Icon name="dots" />}
              onClick={(e) => e.stopPropagation()}
              data-testid="drawer-menu"
            />
          </Dropdown>
        )}

        {/* New unified import input */}
        <input
          ref={fileImportRef}
          type="file"
          accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          style={{ display: "none" }}
          onChange={handleImportChange}
        />

        {/* Legacy docx-only input (kept so old triggers still work if needed) */}
        <input
          ref={fileDocxRef}
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          style={{ display: "none" }}
          onChange={handleDocxChange}
        />

        {onlyView ? (
          <>
            {onClose && (
              <Button
                size="middle"
                type="text"
                onClick={onClose}
                icon={<Icon name="close" />}
              />
            )}
          </>
        ) : isEditing ? (
          <>
            <Button
              size="large"
              onClick={onCancel}
              style={{ marginLeft: 8 }}
              disabled={isLoading}
            >
              Close
            </Button>

            {onPublish && isChanged && (
              <Button
                size="large"
                onClick={onPublish}
                style={{ marginLeft: 8, marginRight: 8 }}
                type="primary"
                data-testid="drawer-publish"
              >
                {isActive && publishButton ? "Publish" : "Save"}
              </Button>
            )}

            {onSave && (
              <Button
                size="large"
                onClick={onSave}
                type="primary"
                loading={isLoading}
                disabled={saveDisabled}
                data-testid={saveTestID}
                style={{ marginLeft: 8, marginRight: 8 }}
              >
                {saveLabel}
              </Button>
            )}
          </>
        ) : (
          <>
            {onEdit && (
              <Button
                type="text"
                size="middle"
                onClick={onEdit}
                icon={<Icon name="edit" />}
              />
            )}
            {onClose && (
              <Button
                size="middle"
                type="text"
                onClick={onClose}
                icon={<Icon name="close" />}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};